import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { firestore } from '../firebaseConfig';
import { useAuth } from '../firebase/useAuth';
import { totalListeningSeconds, avgListens } from '../utils/listeningStats';

const MetadataContext = createContext();

export const useMetadata = () => {
  return useContext(MetadataContext);
};

export const MetadataProvider = ({ children }) => {
  const { user } = useAuth();
  
  // Metadata states
  const [learnedWordIds, setLearnedWordIds] = useState(new Set());
  const [learnedSentenceIds, setLearnedSentenceIds] = useState(new Set());
  const [learnedTodayData, setLearnedTodayData] = useState({});
  const [reviewedWordsData, setReviewedWordsData] = useState({});
  const [reviewedSentencesData, setReviewedSentencesData] = useState({});
  const [playedWordsData, setPlayedWordsData] = useState({});
  const [playedSentencesData, setPlayedSentencesData] = useState({});
  const [listeningStats, setListeningStats] = useState({});
  
  // Stats states
  const [countsByLevel, setCountsByLevel] = useState({});
  const [countsBySrs, setCountsBySrs] = useState({});
  const [reviewAccuracy, setReviewAccuracy] = useState({});
  
  // Loading states
  const [wordIdsLoading, setWordIdsLoading] = useState(true);
  const [sentenceIdsLoading, setSentenceIdsLoading] = useState(true);
  const [learnedTodayLoading, setLearnedTodayLoading] = useState(true);
  const [reviewedWordsLoading, setReviewedWordsLoading] = useState(true);
  const [reviewedSentencesLoading, setReviewedSentencesLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  
  // Error states
  const [error, setError] = useState(null);

  useEffect(() => {
    let unsubscribeMetadata = null;

    if (user) {
      // Reset loading flags so consumers wait for the real data before rendering
      setWordIdsLoading(true);
      setSentenceIdsLoading(true);

      // Single composite listener for all metadata documents
      const metadataRef = collection(firestore, 'users', user.uid, 'metadata');
      
      unsubscribeMetadata = onSnapshot(
        metadataRef,
        (snapshot) => {
          snapshot.docChanges().forEach((change) => {
            const docId = change.doc.id;
            const docData = change.doc.data();
            
            switch (docId) {
              case 'user_words_ids':
                setLearnedWordIds(new Set(docData?.data || []));
                setWordIdsLoading(false);
                break;
                
              case 'user_sentences_ids':
                setLearnedSentenceIds(new Set(docData?.data || []));
                setSentenceIdsLoading(false);
                break;
                
              case 'learned_today':
                setLearnedTodayData(docData?.data || {});
                setLearnedTodayLoading(false);
                break;
                
              case 'reviewed_words_today':
                setReviewedWordsData(docData?.data || {});
                setReviewedWordsLoading(false);
                break;
                
              case 'reviewed_sentences_today':
                setReviewedSentencesData(docData?.data || {});
                setReviewedSentencesLoading(false);
                break;

              case 'played_words_today':
                setPlayedWordsData(docData?.data || {});
                break;

              case 'played_sentences_today':
                setPlayedSentencesData(docData?.data || {});
                break;

              case 'listening_stats':
                setListeningStats(docData || {});
                break;

              case 'counts_by_level':
                setCountsByLevel(docData || {});
                setStatsLoading(false);
                break;
                
              case 'counts_by_srs':
                setCountsBySrs(docData || {});
                break;
                
              case 'review_accuracy':
                setReviewAccuracy(docData || {});
                break;
            }
          });

          // Clear any loading flags that weren't resolved by docChanges —
          // this handles docs that simply don't exist yet in Firestore
          // (new users, users who haven't reviewed today, etc.)
          setWordIdsLoading(false);
          setSentenceIdsLoading(false);
          setLearnedTodayLoading(false);
          setReviewedWordsLoading(false);
          setReviewedSentencesLoading(false);
          setStatsLoading(false);

          setError(null);
        },
        (e) => {
          console.error('Error fetching metadata:', e);
          setError(e);
          setWordIdsLoading(false);
          setSentenceIdsLoading(false);
          setLearnedTodayLoading(false);
          setReviewedWordsLoading(false);
          setReviewedSentencesLoading(false);
          setStatsLoading(false);
        }
      );
    } else {
      // User logged out, clear all data
      setLearnedWordIds(new Set());
      setLearnedSentenceIds(new Set());
      setLearnedTodayData({});
      setReviewedWordsData({});
      setReviewedSentencesData({});
      setPlayedWordsData({});
      setPlayedSentencesData({});
      setListeningStats({});
      setCountsByLevel({});
      setCountsBySrs({});
      setReviewAccuracy({});
      setWordIdsLoading(false);
      setSentenceIdsLoading(false);
      setLearnedTodayLoading(false);
      setReviewedWordsLoading(false);
      setReviewedSentencesLoading(false);
      setStatsLoading(false);
      setError(null);
    }

    return () => {
      if (unsubscribeMetadata) unsubscribeMetadata();
    };
  }, [user]);

  // Derived listening stats — computed here so consumers just read the values.
  // Total seconds (cumulative) for listening time; avg plays/review per type for
  // the "listens before answering" ratio (null when there's no plays data yet).
  const listening = useMemo(() => ({
    totalSeconds: totalListeningSeconds(listeningStats),
    words: {
      seconds: listeningStats?.words?.seconds || 0,
      avgListens: avgListens(playedWordsData, reviewedWordsData),
    },
    sentences: {
      seconds: listeningStats?.sentences?.seconds || 0,
      avgListens: avgListens(playedSentencesData, reviewedSentencesData),
    },
  }), [listeningStats, playedWordsData, playedSentencesData, reviewedWordsData, reviewedSentencesData]);

  const value = {
    // Data
    learnedWordIds,
    learnedSentenceIds,
    learnedTodayData,
    reviewedWordsData,
    reviewedSentencesData,
    playedWordsData,
    playedSentencesData,
    listening,

    // Stats data
    countsByLevel,
    countsBySrs,
    reviewAccuracy,

    // Individual loading states
    wordIdsLoading,
    sentenceIdsLoading,
    learnedTodayLoading,
    reviewedWordsLoading,
    reviewedSentencesLoading,
    statsLoading,
    
    // Combined loading state
    loading: wordIdsLoading || sentenceIdsLoading || learnedTodayLoading || 
             reviewedWordsLoading || reviewedSentencesLoading || statsLoading,
    
    // Error state
    error
  };

  return (
    <MetadataContext.Provider value={value}>
      {children}
    </MetadataContext.Provider>
  );
};